<app-menu>
  <div class="intake-shell" [attr.data-theme]="themeMode">
    <div class="intake-wrapper mx-auto flex min-h-0 max-w-[1900px] flex-col gap-3 px-4 py-3">
      <header class="intake-toolbar">
        <div class="intake-title-stack min-w-0">
          <div class="intake-eyebrow">Inventory / Package Intake</div>
          <div class="flex min-w-0 flex-wrap items-center gap-3">
            <h1 class="truncate">Package Intake</h1>
            <span class="intake-readiness-pill" [ngClass]="readinessClass()">
              <i class="fa-solid" [ngClass]="readinessTone === 'error' ? 'fa-circle-xmark' : readinessTone === 'warning' ? 'fa-triangle-exclamation' : readinessTone === 'ready' ? 'fa-circle-check' : 'fa-circle-info'"></i>
              {{ readinessLabel }}
            </span>
          </div>
          <div class="intake-meta-strip">
            <span><strong>{{ warehouses.length }}</strong> warehouses</span>
            <span><strong>{{ customers.length }}</strong> customers</span>
            <span><strong>{{ templates.length }}</strong> templates</span>
            <span><strong>{{ totalPackages }}</strong> packages</span>
            <span><strong>{{ totalUnits }}</strong> units</span>
          </div>
        </div>

        <div class="intake-toolbar-actions">
          <button type="button" class="intake-button intake-button--ghost" (click)="toggleTheme()" [title]="getThemeLabel() + ' theme'">
            <i [class]="getThemeIcon()"></i>
            <span>{{ getThemeLabel() }}</span>
          </button>
          <button type="button" class="intake-button intake-button--primary" (click)="downloadTemplate()" [disabled]="!selectedTemplate">
            <i class="fa-solid fa-download"></i>
            <span>Template</span>
          </button>
          <button type="button" class="intake-button intake-button--accent" (click)="submitIntake()" [disabled]="!canSubmit">
            <i class="fa-solid" [ngClass]="isSubmitting ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'"></i>
            <span>{{ isSubmitting ? 'Submitting' : (isExistingTripMode ? 'Add To Trip' : 'Create Trip') }}</span>
          </button>
        </div>
      </header>

      <main class="intake-work-grid min-h-0" [formGroup]="intakeForm">
        <aside class="intake-panel intake-setup-panel min-h-0">
          <div class="intake-panel-header">
            <div>
              <div class="intake-eyebrow">Source</div>
              <h2>Setup</h2>
            </div>
            <span class="intake-step-count">{{ completedSetupSteps }} / {{ requiredSetupStepCount }}</span>
          </div>

          <div class="intake-control-list">
            <label class="intake-field">
              <span>Warehouse</span>
              <select formControlName="warehouse_uuid" (change)="onWarehouseChange($any($event.target).value)" [disabled]="isLoadingWarehouses">
                <option value="">{{ isLoadingWarehouses ? 'Loading warehouses...' : 'Select warehouse' }}</option>
                <option *ngFor="let warehouse of warehouses" [value]="warehouseUuid(warehouse)">{{ warehouseLabel(warehouse) }}</option>
              </select>
            </label>

            <label class="intake-field">
              <span>Customer</span>
              <select formControlName="customer_contact_uuid" (change)="onCustomerChange($any($event.target).value)" [disabled]="!intakeForm.get('warehouse_uuid')?.value || isLoadingCustomers">
                <option value="">{{ isLoadingCustomers ? 'Loading customers...' : 'Select customer' }}</option>
                <option *ngFor="let customer of customers" [value]="customerContactUuid(customer)">{{ customerLabel(customer) }}</option>
              </select>
            </label>

            <label class="intake-field">
              <span>Template</span>
              <select formControlName="template_uuid" (change)="onTemplateChange($any($event.target).value)" [disabled]="!intakeForm.get('customer_contact_uuid')?.value || isLoadingTemplates">
                <option value="">{{ isLoadingTemplates ? 'Loading templates...' : 'Select template' }}</option>
                <option *ngFor="let template of templates" [value]="template.upload_template_uuid">{{ template.template_name }}</option>
              </select>
            </label>
          </div>

          <section class="intake-upload-card" [class.is-disabled]="!selectedTemplate">
            <div class="intake-upload-head">
              <div>
                <div class="intake-eyebrow">CSV</div>
                <h3>{{ parsedFileName || 'No file selected' }}</h3>
              </div>
              <span class="intake-mini-status">{{ selectedTemplate?.file_type || 'csv' }}</span>
            </div>

            <div class="intake-upload-actions">
              <input id="package-intake-csv" class="intake-file-input" type="file" accept=".csv,text/csv" [disabled]="!selectedTemplate" (change)="onFileSelected($event)">
              <label class="intake-button intake-button--primary" [class.is-disabled]="!selectedTemplate" for="package-intake-csv">
                <i class="fa-solid fa-file-arrow-up"></i>
                <span>Upload CSV</span>
              </label>
              <button type="button" class="intake-button intake-button--ghost" (click)="downloadTemplate()" [disabled]="!selectedTemplate">
                <i class="fa-solid fa-file-csv"></i>
                <span>Sample</span>
              </button>
            </div>
          </section>

          <section class="intake-mode-card">
            <div class="intake-mode-toggle">
              <button type="button" [class.is-active]="!isExistingTripMode" (click)="setIntakeMode('new_trip')">
                <i class="fa-solid fa-route"></i>
                <span>
                  <strong>New trip + lading</strong>
                  <small>Create the trip, lading, and packages.</small>
                </span>
              </button>
              <button type="button" [class.is-active]="isExistingTripMode" (click)="setIntakeMode('existing_trip')">
                <i class="fa-solid fa-plus"></i>
                <span>
                  <strong>Existing trip</strong>
                  <small>Add packages to a trip that already exists.</small>
                </span>
              </button>
            </div>

            <label class="intake-field intake-existing-trip-field" *ngIf="isExistingTripMode">
              <span>Trip UUID</span>
              <input type="text" formControlName="trip_list_uuid" placeholder="Existing trip_list_uuid">
            </label>
          </section>

          <section class="intake-readiness-card" [ngClass]="readinessClass()">
            <div class="intake-readiness-head">
              <div>
                <span>Readiness</span>
                <strong>{{ readinessLabel }}</strong>
              </div>
              <b>{{ readinessPercent }}%</b>
            </div>
            <div class="intake-progress"><span [style.width.%]="readinessPercent"></span></div>
            <div class="intake-readiness-grid">
              <span><strong>{{ parsedPayload?.lines?.length || 0 }}</strong> lines</span>
              <span><strong>{{ totalPackages }}</strong> packages</span>
              <span><strong>{{ warnings.length }}</strong> warnings</span>
              <span><strong>{{ errors.length }}</strong> errors</span>
            </div>
            <p>{{ submitDisabledReason }}</p>
          </section>

          <section class="intake-issues-card">
            <div class="intake-issues-head">
              <span>Data Checks</span>
              <strong>{{ issueList.length }}</strong>
            </div>
            <div class="intake-issue-list" *ngIf="issueList.length; else cleanIssues">
              <article class="intake-issue" *ngFor="let issue of visibleIssues; trackBy: trackByIssue" [ngClass]="issueClass(issue)">
                <i class="fa-solid" [ngClass]="issue.severity === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation'"></i>
                <div><span>{{ issue.message }}</span></div>
              </article>
              <div class="intake-more-issues" *ngIf="issueList.length > visibleIssues.length">{{ issueList.length - visibleIssues.length }} more issue{{ issueList.length - visibleIssues.length === 1 ? '' : 's' }}</div>
            </div>
            <ng-template #cleanIssues>
              <div class="intake-clean-state">
                <i class="fa-solid fa-circle-check"></i>
                <span>{{ parsedPayload ? 'No blocking issues in this preview.' : 'Waiting for a CSV preview.' }}</span>
              </div>
            </ng-template>
          </section>
        </aside>

        <section class="intake-panel intake-workbench min-h-0">
          <div class="intake-tabs">
            <button type="button" class="intake-tab" [class.is-active]="activeTab === 'lines'" (click)="setActiveTab('lines')">
              <i class="fa-solid fa-table-cells"></i>
              <span>Lines</span>
            </button>
            <button type="button" class="intake-tab" [class.is-active]="activeTab === 'json'" (click)="setActiveTab('json')">
              <i class="fa-solid fa-code"></i>
              <span>JSON</span>
            </button>
            <button type="button" class="intake-tab" [class.is-active]="activeTab === 'result'" (click)="setActiveTab('result')">
              <i class="fa-solid fa-list-check"></i>
              <span>Result</span>
            </button>
          </div>

          <div class="intake-workbench-body" [ngSwitch]="activeTab">
            <section *ngSwitchCase="'lines'" class="intake-tab-panel">
              <div class="intake-workbench-alert" *ngIf="issueList.length" [class.is-error]="errors.length" [class.is-warning]="!errors.length && warnings.length">
                <div class="intake-workbench-alert-head">
                  <i class="fa-solid" [ngClass]="errors.length ? 'fa-circle-xmark' : 'fa-triangle-exclamation'"></i>
                  <div class="min-w-0">
                    <div class="intake-eyebrow">{{ errors.length ? 'Upload Errors' : 'Warnings' }}</div>
                    <h2>{{ errors.length ? 'CSV blocked' : 'Review warnings' }}</h2>
                  </div>
                </div>
                <div class="intake-workbench-alert-list">
                  <article class="intake-workbench-issue" *ngFor="let issue of visibleIssues; trackBy: trackByIssue" [ngClass]="issueClass(issue)">
                    <i class="fa-solid" [ngClass]="issue.severity === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation'"></i>
                    <span>{{ issue.message }}</span>
                  </article>
                </div>
              </div>

              <ng-container *ngIf="parsedPayload; else noPreview">
                <div class="intake-preview-head">
                  <div class="min-w-0">
                    <div class="intake-eyebrow">Selected Preview</div>
                    <h2 class="truncate">{{ parsedFileName }}</h2>
                    <p>{{ selectedWarehouseLabel }} / {{ selectedCustomerLabel }} / {{ selectedTemplateLabel }}</p>
                  </div>
                  <span class="intake-mini-status">{{ intakeModeLabel }}</span>
                </div>

                <div class="intake-count-row">
                  <span *ngFor="let item of headerSummary"><small>{{ item.label }}</small><strong>{{ item.value }}</strong></span>
                </div>

                <div class="intake-table-shell">
                  <table class="intake-table">
                    <thead>
                      <tr>
                        <th>PO</th>
                        <th>Line</th>
                        <th>Item Number</th>
                        <th>Crates</th>
                        <th>Units</th>
                        <th>Deliver Remainder</th>
                        <th>Calculated Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let line of parsedPayload.lines; trackBy: trackByLine">
                        <td>{{ line.po_number }}</td>
                        <td>{{ line.line_number }}</td>
                        <td><strong>{{ line.product_model_number }}</strong></td>
                        <td>{{ line.package_qty }}</td>
                        <td>{{ line.units_per_package }}</td>
                        <td>{{ line.deliver_remainder ?? '-' }}</td>
                        <td>{{ line.package_qty * line.units_per_package }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <details class="intake-raw-details" *ngIf="rawRows.length && csvHeaders.length">
                  <summary>
                    <span>Raw CSV Rows</span>
                    <i class="fa-solid fa-chevron-down"></i>
                  </summary>
                  <div class="intake-table-shell intake-table-shell--raw">
                    <table class="intake-table intake-table--raw">
                      <thead>
                        <tr><th *ngFor="let header of csvHeaders; trackBy: trackByHeader">{{ header }}</th></tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let row of rawRows">
                          <td *ngFor="let header of csvHeaders; trackBy: trackByHeader">{{ rawCellValue(row, header) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </details>
              </ng-container>
            </section>

            <section *ngSwitchCase="'json'" class="intake-tab-panel">
              <div class="intake-code-head">
                <div>
                  <div class="intake-eyebrow">Plugin Payload</div>
                  <h2>Normalized JSON</h2>
                </div>
                <span>{{ parsedPayload?.lines?.length || 0 }} lines</span>
              </div>
              <pre class="intake-json">{{ previewJson }}</pre>
            </section>

            <section *ngSwitchCase="'result'" class="intake-tab-panel">
              <ng-container *ngIf="submitResponse; else noResult">
                <div class="intake-preview-head">
                  <div class="min-w-0">
                    <div class="intake-eyebrow">Plugin Response</div>
                    <h2 class="truncate">{{ resultTitle }}</h2>
                    <p>{{ submitResponse.message || submitDisabledReason }}</p>
                  </div>
                  <span class="intake-mini-status" [class.is-success]="submitResponse.success" [class.is-error]="!submitResponse.success">{{ submitResponse.success ? 'success' : 'failed' }}</span>
                </div>
                <div class="intake-count-row intake-count-row--result">
                  <span><small>Trip</small><strong>{{ submitResponse.trip_number || '-' }}</strong></span>
                  <span><small>Trip UUID</small><strong>{{ submitResponse.trip_list_uuid || '-' }}</strong></span>
                  <span><small>Packages</small><strong>{{ submitResponse.packages_inserted || '-' }}</strong></span>
                  <span><small>Lading</small><strong>{{ submitResponse.lading_uuid || (submitResponse.lading_skipped ? 'Skipped' : (isExistingTripMode ? 'Existing trip' : '-')) }}</strong></span>
                </div>
                <pre class="intake-json">{{ resultJson }}</pre>
              </ng-container>
            </section>
          </div>
        </section>
      </main>
    </div>
  </div>
</app-menu>

<ng-template #noPreview>
  <div class="intake-empty-state" [class.has-issues]="issueList.length">
    <i class="fa-solid" [ngClass]="issueList.length ? 'fa-circle-xmark' : 'fa-file-csv'"></i>
    <span>{{ issueList.length ? 'Resolve the upload issue above, then upload the CSV again.' : 'No CSV preview loaded.' }}</span>
  </div>
</ng-template>

<ng-template #noResult>
  <div class="intake-empty-state">
    <i class="fa-solid fa-list-check"></i>
    <span>No package intake has been submitted in this session.</span>
  </div>
</ng-template>
